import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import { DropdownComponent, SwitchComponent } from '@woolworthsnz/form';
import { BreakPointService, Device, LocalStorageService } from '@woolworthsnz/styleguide';
import { BoostOfferProductTag, FilterCriteria, SortOptionItem } from '@woolworthsnz/trader-api';
import dayjs from 'dayjs';
import { FilterTagsComponent } from '../filter-tags/filter-tags.component';
import { FilterGroupComponent } from '../filter-group/filter-group.component';
import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { SearchCategoryListComponent } from '../search-category-list/search-category-list.component';
import { SearchResultsHeaderComponent } from '../search-results-header/search-results-header.component';
import { SearchQueryParams } from '../models/search-query-params.model';

@Component({
	// tslint:disable-next-line:component-selector
	selector: 'search-pies-header',
	templateUrl: './pies-search-header.component.html',
	styleUrls: ['./pies-search-header.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		AsyncPipe,
		DropdownComponent,
		FilterGroupComponent,
		FilterTagsComponent,
		NgClass,
		NgFor,
		NgIf,
		SearchCategoryListComponent,
		SearchResultsHeaderComponent,
		SwitchComponent,
	],
})
export class PiesSearchHeaderComponent implements OnInit {
	@Input() dasFacets: FilterCriteria[] = [];
	@Input() dasFacetsRoute: string[] = [];
	@Input() dasFacetsQueryParams: SearchQueryParams[] = [];
	@Input() selectedFilters: FilterCriteria[] = [];
	@Input() boostOfferTag: BoostOfferProductTag | undefined;
	@Input() showDynamicContent: boolean;
	@Input() sortOptions: SortOptionItem[] = [];
	@Input() currentSortOption?: SortOptionItem | string;
	@Input() totalItems: number;
	@Input() heading = 'Listing heading';
	@Input() inStockProductsOnlyFromRoute: boolean | undefined;
	@Input() enableSwitchToggleShowInStock = false;
	@Input() enableHideSwitchToggleForOOS = false;
	@Input() shouldShowNewCategoriesList = false;
	@Input() currentShelf: FilterCriteria;

	@ViewChild('searchPiesHeader') piesHeaderElementView: ElementRef;

	@Output() removeFilter = new EventEmitter<FilterCriteria>();
	@Output() removeAllFilters = new EventEmitter<void>();
	@Output() sortChange = new EventEmitter<string>();
	@Output() inStockProductsOnlyToggleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

	myFilterGroups: string[];
	device = Device;
	isNewCoachmarkVisible = true;
	storageKeyNewCoachmarkInSearchPageExpiryInfo = 'newCoachmarkInSearchPageExpiryInfo';
	storageKeyShowInStockProductsOnly = 'showInStockProductsOnly';
	inStockProductsOnlyToggleState = true;
	private _newCoachmarkExpiry = 30 * 24 * 3600 * 1000;

	constructor(public breakPointService: BreakPointService, private localStorageService: LocalStorageService) {}

	get expiryDate(): string {
		const date = this.boostOfferTag?.redemption?.endDate;
		return dayjs(date).format('DD MMMM YYYY');
	}

	get maxRedemptionQuantity(): string {
		const maxQuantity = this.boostOfferTag?.redemption?.total;

		// if maxRedemptionQuantity is equal to the c# int32 max number we have to show unlimited
		return maxQuantity === 2147483647 ? 'Unlimited' : `${maxQuantity} per customer`;
	}

	get redeemedCount(): number {
		return this.boostOfferTag?.redemption
			? (this.boostOfferTag.redemption.total ?? 0) - (this.boostOfferTag.redemption.remaining ?? 0)
			: 0;
	}

	@Input()
	set filterGroups(groups: Array<FilterCriteria[]>) {
		this.myFilterGroups = groups.map((group) => JSON.stringify(group));
	}

	ngOnInit(): void {
		this.setNewCoachmarkExpiry();
		this.setIsNewCoachmarkVisible();

		if (this.enableSwitchToggleShowInStock) {
			this.inStockProductsOnlyToggleState = this.retrieveInStockProductsOnlyLocalStorageValue();
		}
	}

	setIsNewCoachmarkVisible(): void {
		const expiry = this.localStorageService.getItem(this.storageKeyNewCoachmarkInSearchPageExpiryInfo);

		if (!!expiry) {
			const now = new Date();
			if (now.getTime() > expiry) {
				this.isNewCoachmarkVisible = false;
				return;
			}
		}

		this.isNewCoachmarkVisible = true;
	}

	setNewCoachmarkExpiry(): void {
		if (!this.localStorageService.getItem(this.storageKeyNewCoachmarkInSearchPageExpiryInfo)) {
			this.isNewCoachmarkVisible = true;
			this.localStorageService.setItem(
				this.storageKeyNewCoachmarkInSearchPageExpiryInfo,
				JSON.stringify(new Date().getTime() + this._newCoachmarkExpiry)
			);
		}
	}

	handleInStockProductsOnlyToggle(toggleState: boolean): void {
		this.setIsNewCoachmarkVisible();

		this.localStorageService.setItem(this.storageKeyShowInStockProductsOnly, String(toggleState));

		this.inStockProductsOnlyToggleChange.emit(toggleState);
	}

	onRemoveFilter(filterCriteria: FilterCriteria): void {
		this.removeFilter.emit(filterCriteria);
	}

	onRemoveAllFilters(): void {
		this.removeAllFilters.emit();
	}

	handleSortChange(sortValue: string): void {
		this.sortChange.emit(sortValue);
	}

	private retrieveInStockProductsOnlyLocalStorageValue(): boolean {
		if (this.enableHideSwitchToggleForOOS) {
			return false;
		}

		let inStockProductsOnly: boolean;
		if (this.inStockProductsOnlyFromRoute !== undefined) {
			inStockProductsOnly = this.inStockProductsOnlyFromRoute;
		} else {
			const currentValueInLocalStorage = this.localStorageService.getItem(this.storageKeyShowInStockProductsOnly);
			inStockProductsOnly = currentValueInLocalStorage !== undefined ? currentValueInLocalStorage : false;
		}

		this.localStorageService.setItem(this.storageKeyShowInStockProductsOnly, inStockProductsOnly.toString());

		return inStockProductsOnly;
	}
}
